import { useTranslation } from "react-i18next";
import { PanelLeftCloseIcon, PanelLeftOpenIcon } from "lucide-react";
import type React from "react";
import type { User } from "@/features/auth/services/firebase";
import type { UserProfile } from "@/features/auth/contexts/auth/context";
import { cn } from "@/lib/utils";
import { AccountChip } from "./AccountChip";
import { NavGroup } from "./NavGroup";
import { WorkspaceSwitcher } from "./WorkspaceSwitcher";
import { getNavGroups } from "./nav-model";

/**
 * Sidebar — the deep-ink navigation rail: WorkspaceSwitcher, the (real-route)
 * nav groups, and the AccountChip pinned to the bottom. This is a presentational
 * column reused for both the fixed desktop rail and the mobile Sheet drawer;
 * `onNavigate` lets the drawer close itself when a link is followed.
 *
 * `collapsed`/`onToggleCollapsed` drive the desktop icon-only rail (state and
 * persistence live in AppShell). The mobile Sheet instance never passes
 * `onToggleCollapsed` — a temporary overlay drawer has no use for its own
 * collapse toggle — so the button simply doesn't render there.
 */
export function Sidebar({
  user,
  profile,
  pathname,
  onSignOut,
  onNavigate,
  collapsed = false,
  onToggleCollapsed,
}: {
  user: User;
  profile: UserProfile | null;
  pathname: string;
  onSignOut: () => void;
  onNavigate?: () => void;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
}): React.ReactElement {
  const { t } = useTranslation("common");
  const groups = getNavGroups();

  return (
    <div className="flex h-full flex-col bg-[var(--nav-bg)] px-3.5 py-3 text-[var(--nav-ink)]">
      <WorkspaceSwitcher collapsed={collapsed} profile={profile} />
      {onToggleCollapsed && (
        <button
          aria-label={t(
            collapsed ? "shell.expandSidebar" : "shell.collapseSidebar",
          )}
          className={cn(
            "mt-1 flex h-8 items-center gap-2.5 rounded-[var(--r-sm)] text-[var(--nav-ink-2)] transition-colors hover:bg-[var(--nav-bg-2)] hover:text-[var(--nav-ink)]",
            collapsed ? "justify-center px-0" : "px-3",
          )}
          data-test="sidebar-collapse-toggle"
          onClick={onToggleCollapsed}
          type="button"
        >
          {collapsed ? (
            <PanelLeftOpenIcon className="size-[18px] shrink-0" />
          ) : (
            <PanelLeftCloseIcon className="size-[18px] shrink-0" />
          )}
        </button>
      )}
      <div className="my-3.5 h-px bg-[var(--nav-line)]" />
      <nav className="-mr-1.5 flex-1 overflow-y-auto pr-1.5" data-test="sidebar-nav">
        {groups.map((group) => (
          <NavGroup
            key={group.labelKey}
            collapsed={collapsed}
            group={group}
            onNavigate={onNavigate}
            pathname={pathname}
          />
        ))}
      </nav>
      <div className="my-2.5 h-px bg-[var(--nav-line)]" />
      <AccountChip
        collapsed={collapsed}
        onNavigate={onNavigate}
        onSignOut={onSignOut}
        profile={profile}
        user={user}
      />
    </div>
  );
}
